import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDefectRulesets, useDefects } from 'domain/surveyDataManager/hooks';
import { SurveyDataManagerDashboardStatsQuery } from 'domain/surveyDataManager/contracts/dashboardStats';
import { mergeScannerMockCatalogue } from 'domain/surveyDataManager/api/mergeScannerMockCatalogue';
import { mergeDefectOverlayCatalogDefects } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayCatalogs';
import type { ActiveDefectLayer } from 'state/surveyDataManager';

import InlineErrorBanner from '../InlineErrorBanner';
import { humanizeSlug } from '../../utils/formatters';
import WizardBreadcrumbs from './WizardBreadcrumbs';
import WizardFooter from './WizardFooter';
import WizardStepper from './WizardStepper';
import {
  WizardStepDefect,
  WizardStepLane,
  WizardStepParameter,
  WizardStepSurveyType,
  WizardStepSurveyYear,
  WizardStepVisualisation,
} from './steps';
import {
  buildCompletedLayers,
  buildSuggestedLayerDisplayName,
  buildWizardActiveLayerIds,
  buildWizardBreadcrumbs,
  buildWizardStatsQuery,
  buildWizardVisualisationPresets,
  canProceedToNextStep,
  CompletedWizardLayer,
  deriveDefectGroupsForSurveyType,
  deriveParameterOptionsForDefectGroup,
  deriveSurveyTypes,
  isDefectOverlayDefect,
  isWizardDefectGroupFullyActive,
  isWizardMeasurementFullyActive,
  isWizardSurveyYearFullyActive,
  isWizardVisualisationPresetActive,
  resolveCrackmapDefaultLane,
  resolveDefaultVisualisationPreset,
  searchWizardCatalog,
  WizardCatalogSearchMatch,
  WizardParameterOption,
  WizardSelections,
  WizardStepId,
  WizardVisualisationPreset,
} from './wizardLogic';
import { useWizardDialogFocus } from './useWizardDialogFocus';

import './AddLayerWizard.scss';

const AUTO_ADVANCE_MS = 280;

const EMPTY_SELECTIONS: WizardSelections = {
  surveyType: null,
  defectGroup: null,
  defect: null,
  measurement: null,
  surveyYearSlug: null,
  lanes: [],
  visualisationPreset: null,
  colourOverride: null,
  displayName: null,
};

export interface AddLayerWizardProps {
  readonly isOpen: boolean;
  readonly refreshData?: boolean;
  readonly isSubmitting?: boolean;
  readonly activeLayers?: readonly ActiveDefectLayer[];
  readonly onCancel: () => void;
  readonly onComplete: (
    layers: readonly CompletedWizardLayer[],
  ) => void | Promise<void>;
  readonly onStatsFilterChange?: (filter: SurveyDataManagerDashboardStatsQuery) => void;
}

function AddLayerWizard({
  isOpen,
  refreshData = false,
  isSubmitting = false,
  activeLayers = [],
  onCancel,
  onComplete,
  onStatsFilterChange,
}: AddLayerWizardProps): JSX.Element | null {
  const [currentStep, setCurrentStep] = useState<WizardStepId>(1);
  const [surveyTypeSearch, setSurveyTypeSearch] = useState('');
  const [selections, setSelections] = useState<WizardSelections>(EMPTY_SELECTIONS);

  const defectsQuery = useDefects({}, refreshData);
  const rulesetsQuery = useDefectRulesets(
    selections.defect?.slug ?? '',
    refreshData,
    Boolean(selections.defect?.slug) && currentStep >= 5,
  );
  const activeLayerIds = useMemo(() => buildWizardActiveLayerIds(activeLayers), [activeLayers]);
  const isCatalogueLoading =
    defectsQuery.isLoading || (!defectsQuery.data && defectsQuery.isFetching);
  const advanceTimerRef = useRef<number | null>(null);
  const dialogRef = useWizardDialogFocus({
    isOpen,
    isSubmitting,
    onCancel,
  });

  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const resetWizard = () => {
    clearAdvanceTimer();
    setCurrentStep(1);
    setSurveyTypeSearch('');
    setSelections(EMPTY_SELECTIONS);
  };

  const catalogDefects = useMemo(
    () =>
      mergeDefectOverlayCatalogDefects(
        mergeScannerMockCatalogue(defectsQuery.data?.defects ?? []),
      ),
    [defectsQuery.data?.defects],
  );
  const surveyTypes = deriveSurveyTypes(catalogDefects);
  const defects = catalogDefects;
  const catalogMatches = searchWizardCatalog(defects, surveyTypeSearch);

  const query = surveyTypeSearch.trim().toLowerCase();
  const filteredSurveyTypes =
    query.length === 0
      ? surveyTypes
      : surveyTypes.filter(
          (type) =>
            type.toLowerCase().includes(query) ||
            humanizeSlug(type).toLowerCase().includes(query),
        );

  const defectGroups = deriveDefectGroupsForSurveyType(
    defects,
    selections.surveyType,
  );
  const parameterOptions = deriveParameterOptionsForDefectGroup(
    defects,
    selections.surveyType,
    selections.defectGroup,
  );
  const availableSurveyYears = selections.measurement?.surveyYears ?? [];

  const availableLanes =
    selections.surveyYearSlug && selections.measurement
      ? (selections.measurement.surveyYears.find(
          (item) => item.slug === selections.surveyYearSlug,
        )?.lanes ?? [])
      : [];

  const selectedSurveyYear = availableSurveyYears.find(
    (year) => year.slug === selections.surveyYearSlug,
  );
  const parameterLabel = selections.measurement?.name ?? '';
  const surveyYearLabel =
    selectedSurveyYear?.surveyYear ??
    (selections.surveyYearSlug ? String(selections.surveyYearSlug) : '');

  const visualisationPresets = useMemo(
    () => buildWizardVisualisationPresets(rulesetsQuery.data?.rulesets ?? []),
    [rulesetsQuery.data?.rulesets],
  );
  const suggestedDisplayName =
    buildSuggestedLayerDisplayName(selections, availableLanes) ?? '';
  const displayNameValue = selections.displayName ?? suggestedDisplayName;

  const breadcrumbItems = buildWizardBreadcrumbs(
    currentStep,
    selections,
    surveyYearLabel,
    availableLanes,
  );

  useEffect(() => {
    if (!isOpen) {
      clearAdvanceTimer();
      setCurrentStep(1);
      setSurveyTypeSearch('');
      setSelections(EMPTY_SELECTIONS);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!onStatsFilterChange) {
      return;
    }

    const nextFilter = isOpen
      ? buildWizardStatsQuery(selections, surveyYearLabel)
      : {};

    onStatsFilterChange(nextFilter);
  }, [isOpen, onStatsFilterChange, selections, surveyYearLabel]);

  useEffect(() => {
    if (currentStep !== 6 || visualisationPresets.length === 0) {
      return;
    }

    setSelections((prev) => {
      if (
        prev.visualisationPreset &&
        visualisationPresets.some((preset) => preset.slug === prev.visualisationPreset?.slug)
      ) {
        return prev;
      }

      const preferred =
        visualisationPresets.find(
          (preset) => !isWizardVisualisationPresetActive(activeLayerIds, prev, preset),
        ) ?? resolveDefaultVisualisationPreset(visualisationPresets);

      if (!preferred || prev.visualisationPreset?.slug === preferred.slug) {
        return prev;
      }

      return {
        ...prev,
        visualisationPreset: preferred,
        colourOverride: prev.measurement
          ? {
              colorLow: prev.measurement.colorScheme.min,
              colorHigh: prev.measurement.colorScheme.max,
            }
          : null,
        displayName: prev.displayName ?? buildSuggestedLayerDisplayName(prev, availableLanes),
      };
    });
  }, [activeLayerIds, availableLanes, currentStep, visualisationPresets]);

  useEffect(() => () => clearAdvanceTimer(), []);

  const advanceStep = (nextStep: WizardStepId) => {
    setCurrentStep((step) => (nextStep > step ? nextStep : step));
  };

  const scheduleAdvance = (nextStep: WizardStepId) => {
    clearAdvanceTimer();
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      advanceStep(nextStep);
    }, AUTO_ADVANCE_MS);
  };

  const applyCatalogMatch = (
    match: WizardCatalogSearchMatch,
    advanceToParameter: boolean,
  ) => {
    setSelections({
      ...EMPTY_SELECTIONS,
      surveyType: match.surveyType,
      defectGroup: match.defect.type,
    });
    if (advanceToParameter) {
      scheduleAdvance(3);
    }
  };

  const handleSelectCatalogMatch = (match: WizardCatalogSearchMatch) => {
    if (
      isWizardDefectGroupFullyActive(
        activeLayerIds,
        defects,
        match.surveyType,
        match.defect.type,
      )
    ) {
      return;
    }
    applyCatalogMatch(match, true);
  };

  useEffect(() => {
    if (currentStep !== 1 || defectsQuery.isLoading) {
      return;
    }

    const searchQuery = surveyTypeSearch.trim();
    if (searchQuery.length < 3 || catalogMatches.length !== 1) {
      return;
    }

    const soleMatch = catalogMatches[0];
    if (!soleMatch) {
      return;
    }

    const alreadyResolved =
      selections.surveyType === soleMatch.surveyType &&
      selections.defectGroup === soleMatch.defect.type;

    if (alreadyResolved) {
      return;
    }

    const timer = window.setTimeout(() => {
      setSelections({
        ...EMPTY_SELECTIONS,
        surveyType: soleMatch.surveyType,
        defectGroup: soleMatch.defect.type,
      });
      clearAdvanceTimer();
      advanceTimerRef.current = window.setTimeout(() => {
        advanceTimerRef.current = null;
        setCurrentStep((step) => (3 > step ? 3 : step));
      }, AUTO_ADVANCE_MS);
    }, AUTO_ADVANCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    catalogMatches,
    currentStep,
    defectsQuery.isLoading,
    selections.defectGroup,
    selections.surveyType,
    surveyTypeSearch,
  ]);

  const handleSelectSurveyType = (surveyType: string) => {
    setSelections({
      ...EMPTY_SELECTIONS,
      surveyType,
    });
    scheduleAdvance(2);
  };

  const handleSelectDefectGroup = (defectGroup: string) => {
    if (
      isWizardDefectGroupFullyActive(
        activeLayerIds,
        defects,
        selections.surveyType,
        defectGroup,
      )
    ) {
      return;
    }
    setSelections((prev) => ({
      ...prev,
      defectGroup,
      defect: null,
      measurement: null,
      surveyYearSlug: null,
      lanes: [],
      visualisationPreset: null,
      colourOverride: null,
      displayName: null,
    }));
    scheduleAdvance(3);
  };

  const handleSelectParameter = (option: WizardParameterOption) => {
    if (
      isWizardMeasurementFullyActive(
        activeLayerIds,
        option.defect,
        option.measurement,
      )
    ) {
      return;
    }
    setSelections((prev) => ({
      ...prev,
      defect: option.defect,
      measurement: option.measurement,
      surveyYearSlug: null,
      lanes: [],
      visualisationPreset: null,
      colourOverride: null,
      displayName: null,
    }));
    scheduleAdvance(4);
  };

  const handleSelectSurveyYear = (
    surveyYearSlug: WizardSelections['surveyYearSlug'],
  ) => {
    const surveyYear = availableSurveyYears.find((year) => year.slug === surveyYearSlug);
    if (
      !selections.defect ||
      !selections.measurement ||
      !surveyYear ||
      isWizardSurveyYearFullyActive(
        activeLayerIds,
        selections.defect,
        selections.measurement,
        surveyYear,
      )
    ) {
      return;
    }
    setSelections((prev) => ({
      ...prev,
      surveyYearSlug,
      lanes: isDefectOverlayDefect(prev.defect)
        ? [resolveCrackmapDefaultLane(surveyYear)]
        : [],
      visualisationPreset: null,
      colourOverride: null,
      displayName: null,
    }));
    if (!isDefectOverlayDefect(selections.defect)) {
      scheduleAdvance(5);
    }
  };

  const handleSelectLane = (lane: string) => {
    setSelections((prev) => {
      const next = {
        ...prev,
        lanes: [lane],
        visualisationPreset: null,
        colourOverride: null,
      };
      return {
        ...next,
        displayName: buildSuggestedLayerDisplayName(next, availableLanes),
      };
    });
    scheduleAdvance(6);
  };

  const handleSelectVisualisationPreset = (preset: WizardVisualisationPreset) => {
    if (isWizardVisualisationPresetActive(activeLayerIds, selections, preset)) {
      return;
    }
    const measurement = selections.measurement;
    setSelections((prev) => ({
      ...prev,
      visualisationPreset: preset,
      colourOverride: measurement
        ? {
            colorLow: measurement.colorScheme.min,
            colorHigh: measurement.colorScheme.max,
          }
        : null,
      displayName:
        prev.displayName ?? buildSuggestedLayerDisplayName(prev, availableLanes),
    }));
  };

  const handleColourOverrideChange = (
    colourOverride: WizardSelections['colourOverride'],
  ): void => {
    setSelections((prev) => ({
      ...prev,
      colourOverride,
    }));
  };

  const handleDisplayNameChange = (displayName: string) => {
    setSelections((prev) => ({
      ...prev,
      displayName,
    }));
  };

  const handlePrevious = () => {
    setCurrentStep((step) => (step > 1 ? ((step - 1) as WizardStepId) : step));
  };

  const handleNext = () => {
    if (!canProceedToNextStep(currentStep, selections, activeLayerIds)) {
      return;
    }
    if (isDefectOverlayDefect(selections.defect) && currentStep === 4) {
      return;
    }
    setCurrentStep((step) => (step < 6 ? ((step + 1) as WizardStepId) : step));
  };

  const handleComplete = (): void => {
    const layers = buildCompletedLayers(selections, activeLayerIds);
    if (!layers || layers.length === 0 || isSubmitting) {
      return;
    }
    void Promise.resolve(onComplete(layers)).then(() => {
      resetWizard();
    });
  };

  const handleStepClick = (stepId: WizardStepId) => {
    setCurrentStep((step) => (stepId < step ? stepId : step));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <section
      ref={dialogRef}
      className="sdm-add-layer-wizard"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sdm-add-layer-wizard-title"
      tabIndex={-1}
    >
      <header className="sdm-add-layer-wizard__header">
        <div>
          <h3 id="sdm-add-layer-wizard-title" className="sdm-add-layer-wizard__title">
            Add layer
          </h3>
          <p className="sdm-add-layer-wizard__subtitle">
            Choose survey type, parameter, year, lane, and visualisation
          </p>
        </div>
        <button
          type="button"
          className="sdm-add-layer-wizard__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </header>

      <WizardStepper currentStep={currentStep} onStepClick={handleStepClick} />

      <WizardBreadcrumbs items={breadcrumbItems} onStepClick={handleStepClick} />

      <div className="sdm-add-layer-wizard__body" aria-busy={isCatalogueLoading || isSubmitting || undefined}>
        {defectsQuery.isError ? (
          <InlineErrorBanner
            title="Couldn't load defect catalogue"
            error={defectsQuery.error}
            onRetry={() => defectsQuery.refetch()}
          />
        ) : null}

        {isCatalogueLoading ? (
          <div className="sdm-add-layer-wizard__loading" role="status" aria-live="polite">
            <div className="sdm-add-layer-wizard__loading-skeleton" aria-hidden />
            <p className="sdm-add-layer-wizard__loading-text">Loading catalogue…</p>
          </div>
        ) : (
          <>
            {currentStep === 1 ? (
              <WizardStepSurveyType
                surveyTypes={surveyTypes}
                filteredSurveyTypes={filteredSurveyTypes}
                catalogMatches={catalogMatches}
                defects={defects}
                activeLayerIds={activeLayerIds}
                selectedSurveyType={selections.surveyType}
                selectedDefectGroup={selections.defectGroup}
                surveyTypeSearch={surveyTypeSearch}
                isLoading={false}
                onSearchChange={setSurveyTypeSearch}
                onSelectCatalogMatch={handleSelectCatalogMatch}
                onSelectSurveyType={handleSelectSurveyType}
              />
            ) : null}

            {currentStep === 2 ? (
              <WizardStepDefect
                defects={defects}
                surveyType={selections.surveyType}
                defectGroups={defectGroups}
                activeLayerIds={activeLayerIds}
                selectedDefectGroup={selections.defectGroup}
                onSelectDefectGroup={handleSelectDefectGroup}
              />
            ) : null}

            {currentStep === 3 ? (
              <WizardStepParameter
                parameterOptions={parameterOptions}
                activeLayerIds={activeLayerIds}
                selectedDefectSlug={selections.defect?.slug ?? null}
                selectedMeasurementSlug={selections.measurement?.slug ?? null}
                onSelectParameter={handleSelectParameter}
              />
            ) : null}

            {currentStep === 4 ? (
              <WizardStepSurveyYear
                surveyYears={availableSurveyYears}
                defect={selections.defect}
                measurement={selections.measurement}
                activeLayerIds={activeLayerIds}
                selectedSurveyYearSlug={selections.surveyYearSlug}
                onSelectSurveyYear={handleSelectSurveyYear}
              />
            ) : null}

            {currentStep === 5 ? (
              <WizardStepLane
                lanes={availableLanes}
                selections={selections}
                parameterLabel={parameterLabel}
                surveyYearLabel={surveyYearLabel}
                onSelectLane={handleSelectLane}
              />
            ) : null}

            {currentStep === 6 ? (
              <WizardStepVisualisation
                presets={visualisationPresets}
                measurement={selections.measurement}
                selections={selections}
                activeLayerIds={activeLayerIds}
                isLoading={rulesetsQuery.isLoading || rulesetsQuery.isFetching}
                loadErrorMessage={
                  rulesetsQuery.isError && rulesetsQuery.error
                    ? rulesetsQuery.error.message
                    : null
                }
                displayName={displayNameValue}
                suggestedDisplayName={suggestedDisplayName}
                onSelectPreset={handleSelectVisualisationPreset}
                onColourOverrideChange={handleColourOverrideChange}
                onDisplayNameChange={handleDisplayNameChange}
              />
            ) : null}
          </>
        )}
      </div>

      <WizardFooter
        currentStep={currentStep}
        selections={selections}
        activeLayerIds={activeLayerIds}
        isSubmitting={isSubmitting}
        isCatalogueLoading={isCatalogueLoading}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onComplete={handleComplete}
      />
    </section>
  );
}

export default AddLayerWizard;
